import Alert from '@mui/material/Alert';
import FormHelperText from '@mui/material/FormHelperText';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { useMemo } from 'react';
import { type FieldError, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TargetIdTypes } from '../components/TargetIdTypes.tsx';
import { AudienceFields, type AudienceForm } from '../types.ts';
import { shouldShowTargetIdTypes } from '../utils/marketOperatorRestrictions.ts';

export const TargetIdTypesSection = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<AudienceForm>();
  const { errors } = useFormState({ control, name: AudienceFields.ExportTargets });

  const observableExportType = useWatch({ name: AudienceFields.ExportType, control });
  const observableNormalizedDefinition = useWatch({ name: AudienceFields.NormalizedDefinition, control });

  const showTargetIdTypes = useMemo(
    () => shouldShowTargetIdTypes(observableExportType, observableNormalizedDefinition),
    [observableExportType, observableNormalizedDefinition]
  );

  const exportTargetsError = (errors.export_targets as FieldError | undefined)?.message;

  if (!showTargetIdTypes) {
    return null;
  }

  return (
    <FormSection divider title={t('Target ID Types')} rightColumnSize={RIGHT_COLUMN_WIDTH} data-test="target-id-title">
      <TargetIdTypes />
      {exportTargetsError && (
        <FormHelperText error data-test="target-id-types-error" sx={{ ml: 0, mt: 1 }}>
          {exportTargetsError}
        </FormHelperText>
      )}
      <Alert severity="info" data-test="target-id-types-info" sx={{ mt: 2, mb: 2 }}>
        {t(
          'Select the ID types to include in your final audience. Users will be reachable only through the selected ID types.'
        )}
      </Alert>
    </FormSection>
  );
};
